/**
 * exportUtils — CSV, Excel (XLSX) and PDF export helpers.
 * All functions are client-side; no backend calls needed.
 * Used by ExportDropdown and any page that needs multi-format export.
 */
import * as XLSX from "xlsx";

/** Trigger a browser download for a Blob */
function _download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
}

/**
 * Export rows to CSV (semicolon-separated, UTF-8 BOM for Excel compatibility).
 * @param {string[]} headers  - Column labels
 * @param {Array<string[]>} rows - Each row is an array matching headers order
 * @param {string} filename
 */
export function exportCSV(headers, rows, filename) {
  const escape = (v) => {
    const s = String(v ?? "").replace(/"/g, '""');
    return /[;"\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [headers.map(escape).join(";"), ...rows.map(r => r.map(escape).join(";"))];
  const bom = "\uFEFF"; // UTF-8 BOM so Excel opens correctly
  const blob = new Blob([bom + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  _download(blob, filename.endsWith(".csv") ? filename : filename + ".csv");
}

/**
 * Export rows to Excel (.xlsx).
 * @param {string[]} headers
 * @param {Array<string[]>} rows
 * @param {string} filename
 * @param {string} [sheetName]
 */
export function exportExcel(headers, rows, filename, sheetName = "Dados") {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  // Auto column widths
  const colWidths = headers.map((h, i) => ({
    wch: Math.max(h.length, ...rows.map(r => String(r[i] ?? "").length), 10),
  }));
  ws["!cols"] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  _download(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    filename.endsWith(".xlsx") ? filename : filename + ".xlsx");
}

/**
 * Export rows to PDF table.
 * @param {string[]} headers
 * @param {Array<string[]>} rows
 * @param {string} filename
 * @param {string} [title]
 */
export async function exportPDF(headers, rows, filename, title = "") {
  const { default: jsPDF } = await import("jspdf");
  const { autoTable } = await import("jspdf-autotable");
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  if (title) {
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(title, 14, 16);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`Gerado em ${new Date().toLocaleString("pt-PT")}`, 14, 22);
    doc.setTextColor(0);
  }

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: title ? 28 : 14,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [205, 163, 79], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    margin: { left: 14, right: 14 },
  });

  doc.save(filename.endsWith(".pdf") ? filename : filename + ".pdf");
}

/**
 * Parse a raw CSV string (semicolon or comma separated) into [headers, rows].
 * Used when downloading CSV from backend and re-exporting as Excel/PDF.
 */
export function parseCSV(text) {
  const sep = text.includes(";") ? ";" : ",";
  const lines = text.trim().split(/\r?\n/);
  const headers = lines[0].split(sep).map(h => h.trim().replace(/^"|"$/g, ""));
  const rows = lines.slice(1).map(l => l.split(sep).map(v => v.trim().replace(/^"|"$/g, "")));
  return { headers, rows };
}
